import { View } from "tamagui";
import { resolveShimmerStyle, SHIMMER_DURATION_MS, type SkeletonDimension, type SkeletonRadius, type SkeletonShimmerProps } from "./SkeletonShimmer.shared";
export type { SkeletonDimension, SkeletonRadius, SkeletonShimmerProps } from "./SkeletonShimmer.shared";

// Animated skeleton block with a soft sweeping highlight — gentler than moti's
// default shimmer. Web keyframe variant; native mirrors via reanimated.
export function SkeletonShimmer({
  width = "100%",
  height = "100%",
  radius = 8
}: SkeletonShimmerProps) {
  const {
    rest,
    highlight,
    restTransparent,
    radiusPx
  } = resolveShimmerStyle(radius);
  return <>
      {/* href + precedence let React hoist and dedupe this to one copy per document. */}
      <style href="skeleton-shimmer" precedence="default">{`
        @keyframes skeleton-shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .skeleton-shimmer {
          animation: skeleton-shimmer ${SHIMMER_DURATION_MS / 1000}s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .skeleton-shimmer { display: none; }
        }
      `}</style>
      <View width={width} height={height} borderRadius={radiusPx} overflow="hidden" style={{
      backgroundColor: rest
    }} data-sentry-element="View" data-sentry-source-file="SkeletonShimmer.tsx">
        {/* Raw div: Tamagui strips className, and the sweep must land in the DOM. */}
        <div className="skeleton-shimmer" style={{
        position: "absolute",
        inset: 0,
        backgroundImage: `linear-gradient(90deg, ${restTransparent} 0%, ${highlight} 50%, ${restTransparent} 100%)`
      }} />
      </View>
    </>;
}

// A shaped placeholder block (avatar, button, card region) — takes an explicit
// height and radius so skeletons can mirror real layouts. Prefer `$si` size
// tokens over raw px.
export const SkeletonBlock = ({
  width = "100%",
  height,
  radius = 8
}: {
  width?: SkeletonDimension;
  height: SkeletonDimension;
  radius?: SkeletonRadius;
}) => <SkeletonShimmer width={width} height={height} radius={radius} data-sentry-element="SkeletonShimmer" data-sentry-component="SkeletonBlock" data-sentry-source-file="SkeletonShimmer.tsx" />;